import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, Loader2, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ label, value }) => (
  <div className="flex justify-between py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</span>
    <span className="text-sm font-semibold text-slate-900 text-right" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{value || '—'}</span>
  </div>
);

const CarrierTrustPage = () => {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/carrier/${companyId}`);
        setData(res.data);
        const name = res.data.company_name || 'Carrier';
        document.title = `${name} — FMCSA Verified Carrier | CivilX TMS`;
        const setMeta = (attr, key, val) => {
          let el = document.querySelector(`meta[${attr}="${key}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
          el.setAttribute('content', val);
        };
        const desc = `${name} — DOT #${res.data.dot_number || ''} · ${res.data.carrier?.operating_status || ''}. FMCSA authority verified via CivilX TMS.`;
        setMeta('property', 'og:title', `${name} — FMCSA Verified Carrier`);
        setMeta('property', 'og:description', desc);
        setMeta('name', 'description', desc);
      } catch (e) {
        setError(e.response?.status === 404 ? 'No verified carrier information is available for this company.' : 'Unable to load carrier information.');
      } finally { setLoading(false); }
    })();
    return () => { document.title = 'CivilX TMS — Fleet & Dispatch'; };
  }, [companyId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="trust-page-error">
      <div className="text-center text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>
    </div>
  );

  const c = data.carrier;
  const good = !c.out_of_service && c.insurance_ok !== false;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="carrier-trust-page">
      <div className="max-w-lg mx-auto">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/assets/civilx-tms-logo.png" alt="CivilX TMS" className="h-9 w-auto mb-4" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{data.company_name}</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Carrier Verification</p>
        </div>

        <div className={`rounded-xl border overflow-hidden ${good ? 'border-green-200' : 'border-red-200'}`}>
          <div className={`px-6 py-4 flex items-center gap-3 ${good ? 'bg-green-50' : 'bg-red-50'}`}>
            {good ? <ShieldCheck className="w-6 h-6 text-green-600" strokeWidth={2.5} /> : <ShieldAlert className="w-6 h-6 text-red-600" strokeWidth={2.5} />}
            <div>
              <div className={`text-sm font-bold ${good ? 'text-green-800' : 'text-red-800'}`} style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="trust-status">
                {good ? 'FMCSA Verified · Authorized' : c.operating_status}
              </div>
              <div className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Source: FMCSA (SAFER) · public record</div>
            </div>
          </div>
          <div className="bg-white px-6 py-3">
            <Row label="Legal name" value={c.legal_name} />
            <Row label="DBA" value={c.dba_name} />
            <Row label="US DOT #" value={data.dot_number} />
            <Row label="MC / Docket #" value={data.mc_number} />
            <Row label="Operating status" value={c.operating_status} />
            <Row label="Insurance on file" value={c.insurance_ok ? 'Yes' : 'Attention needed'} />
            <Row label="Common authority" value={c.common_authority} />
            <Row label="Contract authority" value={c.contract_authority} />
            <Row label="Operation" value={c.carrier_operation} />
            <Row label="Safety rating" value={c.safety_rating} />
            <Row label="Power units" value={c.total_power_units} />
            <Row label="Drivers" value={c.total_drivers} />
            <Row label="Domiciled" value={[c.phy_city, c.phy_state].filter(Boolean).join(', ')} />
            <Row label="Last verified" value={c.checked_at ? new Date(c.checked_at).toLocaleDateString() : null} />
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6 flex items-center justify-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <Truck className="w-3 h-3" /> Verified via FMCSA QCMobile · Powered by CivilX TMS
        </p>
      </div>
    </div>
  );
};

export default CarrierTrustPage;
